import React, { useEffect } from 'react';

import IProduct from 'models/product';
import { FloatInputDebounced } from '../FloatInput';
import ThreeWaySwitch from 'components/inputfields/ThreeWaySwitch';
import { IFoodlaCategoryOption } from 'models/category';
import { getApoteaMarginSek, formatApoteaMargin, getEffectiveApoteaVat } from 'utils/apoteaMargin';
import { toSafeFloat } from 'utils/helpers';
import { useAppContext } from 'AppContext';
import CircularSpinner from 'components/loadingIndicator/CircularSpinner';

interface IYarnProps {
  state: IProduct;
  approve?: boolean;
  setState: React.Dispatch<React.SetStateAction<IProduct>>;
  readOnly?: boolean;
  rootCategory?: IFoodlaCategoryOption;
}

export const ApoteaPrices = ({ state, setState, readOnly, approve, rootCategory }: IYarnProps) => {
  const styles = { marginBottom: 0, width: 370, flex: 'none', paddingBottom: 10 };

  const { loadingExchangeRates, exchangeRates, setSkipExchangeRates } = useAppContext();
  const { percent: margin, date: rateDate, missingRateCurrency } = getApoteaMarginSek(state, rootCategory, exchangeRates);
  const marginInputsFilled =
    toSafeFloat(state.apoteaStorePrice, null) != null &&
    toSafeFloat(state.apoteaPurchasePrice, null) != null &&
    getEffectiveApoteaVat(state, rootCategory) != null;
  
  useEffect(() => {
    setSkipExchangeRates(false);
  }, [setSkipExchangeRates]);

  return (
    <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
            <FloatInputDebounced<IProduct>
              readOnly={readOnly}
              dense={false}
              label="Listpris"
              placeholder="Ex. 294"
              name="apoteaListPrice"
              state={state}
              setState={setState}
              style={styles}
              shrink={readOnly}
            />
            <h3 style={{ alignSelf: 'center' }}>&nbsp;{state.producerApoteaPriceCurrency}</h3>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
            <FloatInputDebounced<IProduct>
              readOnly={readOnly}
              dense={false}
              label="Apoteas inköpspris"
              placeholder="Ex. 162"
              name="apoteaPurchasePrice"
              state={state}
              setState={setState}
              style={styles}
              shrink={readOnly}
            />
            <h3 style={{ alignSelf: 'center' }}>&nbsp;{state.producerApoteaPriceCurrency}</h3>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
              <FloatInputDebounced<IProduct>
                readOnly={readOnly}
                dense={false}
                label="Rekommenderat utpris i SEK (inkl. moms)"
                placeholder="Ex. 294"
                name="apoteaStorePrice"
                shrink={readOnly}
                state={state}
                setState={setState}
                style={styles}
              />
              <h3 style={{ alignSelf: 'center' }}>&nbsp;SEK</h3>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
              <FloatInputDebounced<IProduct>
                readOnly={readOnly}
                dense={false}
                label="Rekommenderat utpris i NOK (inkl. moms)"
                placeholder="Ex. 294"
                name="apoteaStorePriceNok"
                shrink={readOnly}
                state={state}
                setState={setState}
                style={styles}
              />
              <h3 style={{ alignSelf: 'center' }}>&nbsp;NOK</h3>
            </div>
          </div>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <div style={{ fontWeight: 500 }}>Marginal SEK:</div>
            {loadingExchangeRates ? (
              <CircularSpinner size={14} />
            ) : (
              <>
                <div style={{ fontWeight: 700 }}>{formatApoteaMargin(margin)}</div>
                {rateDate && margin !== null && (
                  <div style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
                    (Beräknad med ECB-kurs från {rateDate})
                  </div>
                )}
              </>
            )}
          </div>
          
          {margin == null && missingRateCurrency && marginInputsFilled ? (
            <div style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
              ECB-kurs för {missingRateCurrency} saknas, marginalen kan inte beräknas just nu.
            </div>
          ) : margin == null ? (
            <div style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
              Fyll i utpris, inköpspris och moms för att se marginal.
            </div>
          ) : margin > 0 ? (
            <div style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
              Baseras på rekommenderat utpris (inkl. moms), Apoteas inköpspris och moms.
            </div>
          ) : (
            <div style={{  color: '#FF0000' }}>
              Marginalen är 0 % eller negativ. Kontrollera Apoteas inköpspris och rekommenderat utpris (inkl. moms).
            </div>
          )}
        </div>
      </div>
      {approve && state?.approve?.find(x => x.field === 'apoteaPrices') && (
        <ThreeWaySwitch
          readOnly={readOnly}
          state={state}
          setState={setState}
          name={"apoteaPrices" as keyof IProduct}
        />
      )}
    </div>
  );
};

export default ApoteaPrices;
